import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Notification } from '../../types/user';
import BellIcon from '../icons/BellIcon';

// NotificationDialog props 인터페이스
interface NotificationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onMarkAsRead?: (notificationId: string) => void;
  onMarkAllAsRead?: () => void;
  onDeleteNotification?: (notificationId: string) => void;
  onViewAll?: () => void;
  scale?: number; // 스케일 비율 추가
  notifications?: Notification[];
}

const NotificationDialog: React.FC<NotificationDialogProps> = ({
  isOpen,
  onClose,
  onMarkAsRead,
  onMarkAllAsRead,
  onDeleteNotification,
  onViewAll,
  scale = 1,
  notifications = []
}) => {
  const [localNotifications, setLocalNotifications] = useState<Notification[]>(notifications);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    setLocalNotifications(notifications);
  }, [notifications]);

  const unreadCount = useMemo(() => (
    localNotifications.filter(n => !n.isRead).length
  ), [localNotifications]);

  // 알림 읽음 처리
  const handleMarkAsRead = (notificationId: string) => {
    setLocalNotifications(prev => 
      prev.map(notification => 
        notification.id === notificationId 
          ? { ...notification, isRead: true }
          : notification
      )
    );
    onMarkAsRead?.(notificationId);
  };

  const handleDelete = (notificationId: string) => {
    setLocalNotifications(prev => prev.filter(notification => notification.id !== notificationId));
    onDeleteNotification?.(notificationId);
  };

  // 모든 알림 읽음 처리
  const handleMarkAllAsRead = () => {
    setLocalNotifications(prev => 
      prev.map(notification => ({ ...notification, isRead: true }))
    );
    onMarkAllAsRead?.();
  };

  // 시간 포맷팅 함수
  const formatTimeAgo = (date: Date) => {
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    
    if (diffInSeconds < 60) return '방금 전';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}분 전`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}시간 전`;
    return `${Math.floor(diffInSeconds / 86400)}일 전`;
  };

  // 알림 타입별 색상
  const getTypeColor = (type: Notification['type']) => {
    switch (type) {
      case 'point': return '#4CAF50';
      case 'item': return '#9C27B0';
      case 'role': return '#FF9800';
      case 'system': return '#2196F3';
      case 'approval': return '#FF9800';
      default: return '#607D8B';
    }
  };

  // 알림 타입별 아이콘
  const getTypeIcon = (type: Notification['type']) => {
    switch (type) {
      case 'point': return '💰';
      case 'item': return '📦';
      case 'role': return '👤';
      case 'system': return 'ℹ️';
      case 'approval': return '✅';
      default: return '📢';
    }
  };

  if (!isOpen) return null;

  return (
    
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      fontFamily: 'Pretendard',
      zIndex: 1000,
      padding: '0 1.25rem'
    }}>
      <div style={{
        background: 'var(--Color-background-normal, white)',
        borderRadius: '1rem',
        width: '24.625rem',
        height: '45.8125rem',
        boxShadow: '0 8px 16px rgba(0, 0, 0, 0.12)',
        padding: '1.5rem 1.75rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '2rem'
      }}>
        {/* 헤더 */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: '0.5rem',
          flexDirection: 'row'
        }}>
          <div style={{
            width: '22.125rem',
            display: 'flex',
            alignItems: 'flex-start',
            flexDirection: 'column',
          }}>
            <h1 style={{
            margin: 0,
            fontSize: '1.25rem',
            fontWeight: '600',
            lineHeight: '1.875rem',
            color: '#161616',
            wordWrap: 'break-word'
            }}>
            알림
            </h1>
            <span style={{
              color: '#989898',
              fontSize: '1rem',
              fontWeight: '500',
              lineHeight: '1.5rem',
              wordWrap: 'break-word'
            }}>
              총 {localNotifications.length}개의 알림이 있습니다.
            </span>
          </div>
          <div style={{
            display: 'flex',
            width: '2rem',
            height: '100%',
            justifyContent: 'flex-start',
            alignItems: 'flex-start',
            padding: '0.25rem'
          }}>
            <img src={"/images/icons/closeIcon.svg"}
              onClick={onClose}
              style={{
                width: '1.5rem',
                height: '1.5rem',
                padding: '0.3375rem',
                cursor: 'pointer'
              }}
            >
            </img>
          </div>
        </div>
        {/* 새로운 알림 수 */}
        <div style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          fontSize: '1rem',
          fontWeight: 600,
          gap: '0.25rem'
        }}>
          <span style={{ color: 'var(--Color-text-brand-color, #FF6F0F)' }}>{unreadCount}개</span>
          <span style={{ color: 'var(--Color-text-normal, #161616)' }}>의 새로운 알림</span>
        </div>

        {/* 알림 목록 */}
        <div style={{
          flex: '1 1 auto',
          width: '100%',
          overflowY: 'auto',
          paddingRight: '0.25rem',
            display: 'flex',
            flexDirection: 'column',
          gap: '0.75rem'
        }}>
          {localNotifications.length === 0 && (
            <div
              style={{
            width: '100%',
                padding: '5rem 0',
            display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '0.75rem',
                color: '#989898'
              }}
            >
              <BellIcon width="3rem" height="3rem" fill="rgba(13, 13, 13, 0.4)" />
              <span style={{ fontSize: '0.875rem' }}>표시할 알림이 없습니다.</span>
            </div>
          )}

          {localNotifications.map((notification) => (
            <div
              key={notification.id}
              onMouseEnter={() => setHoveredId(notification.id)}
              onMouseLeave={() => setHoveredId(null)}
              style={{
                position: 'relative',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: '0.5rem',
                padding: '0.9375rem 0'
              }}
            >
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '0.5rem'
            }}>
                <div style={{
                    display: 'flex',
                  alignItems: 'center',
                  gap: '0.5625rem'
                }}>
                  <span style={{
                    color: '#161616',
                    fontSize: '1rem',
                    fontWeight: 600
                }}>
                    {notification.title}
                  </span>
                  {!notification.isRead && (
                    <div style={{
                      width: '0.5rem',
                      height: '0.5rem',
                      borderRadius: '50%',
                      backgroundColor: getTypeColor(notification.type)
                    }} />
                  )}
                            </div>
                <span style={{
                            color: '#989898',
                  fontSize: '0.875rem',
                  fontWeight: 400
                        }}>
                  {formatTimeAgo(notification.createdAt)}
                </span>
                    </div>
                    <div style={{
                color: '#4C4C4C',
                fontSize: '0.875rem',
                fontWeight: 400,
                lineHeight: '1.3125rem'
              }}>
                {notification.message}
              </div>

              {/* Hover action buttons */}
              {hoveredId === notification.id && (
                <div style={{
                  position: 'absolute',
                  top: '1rem',
                  right: '0',
                        display: 'flex',
                  gap: '0.5rem',
                  background: 'rgba(255,255,255,0.95)',
                  padding: '0.375rem 0.625rem',
                  borderRadius: '999px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
                }}>
                  {notification.actionUrl && (
                    <button
                      style={{
                        padding: '0.375rem 0.75rem',
                        borderRadius: '999px',
                        border: '1px solid #E0E0E0',
                        backgroundColor: '#FFFFFF',
                        color: '#4C4C4C',
                        fontSize: '0.75rem',
                        cursor: 'pointer'
                      }}
                      onClick={() => {
                        navigate(notification.actionUrl as string);
                        onClose();
                      }}
                    >
                      바로가기
                    </button>
                  )}
                  <button
                    style={{
                      padding: '0.375rem 0.75rem',
                      borderRadius: '999px',
                      border: 'none',
                      backgroundColor: notification.isRead ? '#F0F0F0' : 'var(--Color-fill-brand-color, #FF6F0F)',
                      color: notification.isRead ? '#4C4C4C' : '#FFFFFF',
                      fontSize: '0.75rem',
                      cursor: 'pointer'
                    }}
                    onClick={() => handleMarkAsRead(notification.id)}
                  >
                    읽음 표시
                  </button>
                  <button
                    style={{
                      padding: '0.375rem 0.75rem',
                      borderRadius: '999px',
                      border: '1px solid #E57373',
                      backgroundColor: '#FFFFFF',
                      color: '#E57373',
                      fontSize: '0.75rem',
                      cursor: 'pointer'
                    }}
                    onClick={() => handleDelete(notification.id)}
                  >
                    삭제
                  </button>
                </div>
              )}

              <div style={{
                width: '100%',
                height: '0.0625rem',
                background: 'var(--Color-line-normal, #F0F0F0)'
              }} />
            </div>
          ))}
        </div>

        {/* 하단 버튼 */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '0.5rem'
        }}>
          <button
            style={{
              padding: '0.625rem 1.25rem',
              borderRadius: '0.5rem',
              border: 'none',
              backgroundColor: unreadCount === 0 ? '#F0F0F0' : '#293044',
              color: unreadCount === 0 ? '#989898' : '#FFFFFF',
              fontSize: '0.8125rem',
              fontWeight: 500,
              cursor: unreadCount === 0 ? 'default' : 'pointer'
            }}
            onClick={() => unreadCount > 0 && handleMarkAllAsRead()}
          >
            모두 읽음 처리
          </button>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            {onViewAll && (
              <button
                type="button"
                style={{
                  padding: '0.625rem 1.25rem',
                  borderRadius: '0.5rem',
                  border: 'none',
                  backgroundColor: 'var(--Color-fill-brand-color, #FF6F0F)',
                  color: '#FFFFFF',
                  fontSize: '0.8125rem',
                  fontWeight: 500,
                  cursor: 'pointer'
                }}
                onClick={onViewAll}
              >
                전체 보기
              </button>
            )}
            <button
              type="button"
              style={{
                padding: '0.625rem 1.25rem',
                borderRadius: '0.5rem',
                border: '1px solid #E0E0E0',
                backgroundColor: '#FFFFFF',
                color: '#4C4C4C',
                fontSize: '0.8125rem',
                fontWeight: 500,
                cursor: 'pointer'
              }}
              onClick={onClose}
            >
              닫기
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationDialog;
